import { RequestData, ResponseData, KeyValue, TestResult, ScriptLog } from "../types";

export interface ScriptContext {
  request: RequestData;
  response?: ResponseData;
  variables: KeyValue[];
  onVariableUpdate: (key: string, value: string) => void;
  onVariableUnset: (key: string) => void;
}

export interface ScriptExecutionResult {
  request: RequestData;
  testResults: TestResult[];
  logs: ScriptLog[];
  error?: string;
}

export async function runScript(
  script: string,
  context: ScriptContext
): Promise<ScriptExecutionResult> {
  const logs: ScriptLog[] = [];
  const testResults: TestResult[] = [];

  const pm = {
    request: {
      url: context.request.url,
      method: context.request.method,
      headers: {
        all: () => context.request.headers,
        get: (key: string) => context.request.headers.find(h => h.key.toLowerCase() === key.toLowerCase())?.value,
        add: (header: { key: string; value: string }) => {
          context.request.headers.push({ id: Math.random().toString(36).substring(2), ...header, enabled: true });
        },
        remove: (key: string) => {
          context.request.headers = context.request.headers.filter(h => h.key.toLowerCase() !== key.toLowerCase());
        }
      },
      body: context.request.body
    },
    response: context.response ? {
      status: context.response.status,
      statusText: context.response.statusText,
      headers: context.response.headers,
      json: () => {
        try {
          return typeof context.response?.data === 'string' ? JSON.parse(context.response.data) : context.response?.data;
        } catch (e) {
          return null;
        }
      },
      text: () => typeof context.response?.data === 'string' ? context.response.data : JSON.stringify(context.response?.data)
    } : undefined,
    environment: {
      get: (key: string) => context.variables.find(v => v.key === key)?.value,
      set: (key: string, value: string) => context.onVariableUpdate(key, value),
      unset: (key: string) => context.onVariableUnset(key)
    },
    test: (name: string, fn: () => void) => {
      try {
        fn();
        testResults.push({ name, passed: true });
      } catch (e: any) {
        testResults.push({ name, passed: false, message: e.message });
      }
    },
    expect: (value: any) => {
      const expectation = {
        to: {
          equal: (expected: any) => {
            if (value !== expected) throw new Error(`Expected ${expected} but got ${value}`);
          },
          be: {
            true: () => {
              if (value !== true) throw new Error(`Expected true but got ${value}`);
            },
            null: () => {
              if (value !== null) throw new Error(`Expected null but got ${value}`);
            }
          },
          have: {
            status: (expected: number) => {
              if (value !== expected) throw new Error(`Expected status ${expected} but got ${value}`);
            }
          },
          not: {
            be: {
              null: () => {
                if (value === null) throw new Error(`Expected not null`);
              }
            }
          }
        }
      };
      return expectation;
    }
  };

  const customConsole = {
    log: (...args: any[]) => logs.push({ type: "log", message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(" "), timestamp: Date.now() }),
    error: (...args: any[]) => logs.push({ type: "error", message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(" "), timestamp: Date.now() }),
    warn: (...args: any[]) => logs.push({ type: "warn", message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(" "), timestamp: Date.now() }),
    info: (...args: any[]) => logs.push({ type: "info", message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(" "), timestamp: Date.now() }),
  };

  try {
    // Use new Function for a simple sandbox-like execution
    // In a real app, a Web Worker or a more robust sandbox would be better
    const execute = new Function("pm", "console", script);
    execute(pm, customConsole);
  } catch (e: any) {
    logs.push({ type: "error", message: `Script Error: ${e.message}`, timestamp: Date.now() });
    return {
      request: context.request,
      testResults,
      logs,
      error: e.message
    };
  }

  return {
    request: context.request,
    testResults,
    logs
  };
}
